import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PnlBasis } from '@tailoring-crm/shared';
import { ReportChart } from '../components/reports/report-chart.js';
import type { RangePickerValue } from '../components/reports/range-picker.js';
import { ReportShell, StatTile } from '../components/reports/report-shell.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useHasModule } from '../lib/billing/entitlements.js';
import { formatCurrency, formatNumber } from '../lib/format.js';
import { financialReportCsvPath } from '../lib/reports/reports-api.js';
import { useFinancialReport } from '../lib/reports/use-reports.js';
import { cn } from '@tailoring-crm/ui';

/**
 * Profit over any period, what customers still owe, and what the shop still owes its karigars.
 *
 * THE OWNER'S ALONE. What the shop actually earns is not a manager's business any more than a
 * karigar's, and the permission matrix has said so since task 4 — so a manager reaching this URL
 * is told plainly rather than shown a page of refused requests.
 *
 * The basis toggle is the same one the monthly P&L carries and means exactly the same thing,
 * because both screens send the same parameter to services that read it the same way. A shop
 * carrying large unpaid balances gets two different profit figures and both are true; what would
 * be indefensible is two screens quietly disagreeing about which one they are showing.
 *
 * Receivables are aged from the BOOKING, so a six-month-old order that was never finished shows
 * up in the oldest bucket instead of hiding behind a delivery date it never reached.
 */
export function FinancialReportPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [range, setRange] = useState<RangePickerValue>({ preset: 'this_month' });
  const [basis, setBasis] = useState<PnlBasis>('cash');

  const allowed = role === 'owner';
  const inPlan = useHasModule('advanced_analytics');
  const params = { ...range, basis };
  const report = useFinancialReport(params, { enabled: allowed && inPlan });
  const data = report.data;

  return (
    <ReportShell
      title={t('reports.financial.title')}
      description={t('reports.financial.description')}
      range={range}
      onRangeChange={setRange}
      resolved={data?.range}
      csvPath={financialReportCsvPath(params)}
      isPending={report.isPending && allowed && inPlan}
      allowed={allowed}
      module="advanced_analytics"
      controls={
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t('pnl.basis')}</span>
          <div role="radiogroup" aria-label={t('pnl.basis')} className="flex gap-2">
            {(['cash', 'accrual'] as const).map((one) => (
              <button
                key={one}
                type="button"
                role="radio"
                aria-checked={basis === one}
                onClick={() => setBasis(one)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  basis === one
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border hover:bg-accent',
                )}
              >
                {t(`pnl.basisLabel.${one}`)}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {data ? (
        <>
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm" role="status">
            <strong className="font-semibold">{t(`pnl.basisLabel.${data.basis}`)}</strong>{' '}
            {t(`pnl.basisExplainer.${data.basis}`)}
          </p>

          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label={t('pnl.revenue')}
              value={formatCurrency(data.totals.revenuePaise)}
              hint={t('pnl.entryCount', { count: data.totals.revenueEntryCount })}
            />
            <StatTile
              label={t('pnl.expenses')}
              value={formatCurrency(data.totals.expensePaise)}
              hint={t('pnl.entryCount', { count: data.totals.expenseEntryCount })}
            />
            <StatTile label={t('pnl.profit')} value={formatCurrency(data.totals.profitPaise)} />
            <StatTile
              label={t('reports.financial.wagesOwed')}
              value={formatCurrency(data.wageLiability.totalPaise)}
              hint={t('reports.financial.staffCount', { count: data.wageLiability.staffCount })}
            />
          </dl>

          <ReportChart
            title={t('reports.financial.byMonth')}
            labelHeading={t('reports.financial.month')}
            valueHeading={t('pnl.profit')}
            initialView="table"
            points={data.byMonth.map((row) => ({
              label: row.month,
              value: Math.max(0, row.profitPaise),
              display: formatCurrency(row.profitPaise),
            }))}
          />

          <section className="flex flex-col gap-2" aria-label={t('reports.financial.receivables')}>
            <h2 className="text-sm font-semibold">{t('reports.financial.receivables')}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th scope="col" className="py-1 font-medium">
                    {t('reports.financial.age')}
                  </th>
                  <th scope="col" className="py-1 text-right font-medium">
                    {t('reports.financial.orders')}
                  </th>
                  <th scope="col" className="py-1 text-right font-medium">
                    {t('reports.financial.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.receivables.buckets.map((bucket) => (
                  <tr key={bucket.key} className="border-t border-border">
                    <th scope="row" className="py-1.5 text-left font-normal">
                      {t('reports.financial.ageBucket', { key: bucket.key })}
                    </th>
                    <td className="py-1.5 text-right tabular-nums">
                      {formatNumber(bucket.orderCount)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {formatCurrency(bucket.amountPaise)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-border font-semibold">
                  <th scope="row" className="py-1.5 text-left">
                    {t('reports.financial.totalOwed')}
                  </th>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatNumber(data.receivables.orderCount)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatCurrency(data.receivables.totalPaise)}
                  </td>
                </tr>
              </tbody>
            </table>
            <Link
              to="/payments?filter=outstanding"
              className="self-start text-sm text-primary underline underline-offset-2"
            >
              {t('reports.financial.openOutstanding')}
            </Link>
          </section>

          <ReportChart
            title={t('pnl.byCategory')}
            labelHeading={t('reports.financial.category')}
            valueHeading={t('reports.financial.amount')}
            initialView="table"
            points={data.byCategory.map((row) => ({
              label: row.categoryLabel,
              value: row.amountPaise,
              display: formatCurrency(row.amountPaise),
            }))}
          />
        </>
      ) : null}
    </ReportShell>
  );
}

export default FinancialReportPage;
